import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { surfaceSx } from "../center/ownerCenterUi";

const JOURNEY = [
  ["cold", "Nuevo", "Todavía no hubo contacto"],
  ["outreach", "Primer mensaje", "Ya salió el primer contacto"],
  ["conversation", "Conversando", "Hay ida y vuelta"],
  ["qualified", "Interesado", "Hay necesidad e interés claros"],
  ["proposal", "Propuesta", "Presupuesto/propuesta enviada"],
  ["decision", "Decisión", "Negociación o definición"],
  ["won", "Cerrado", "Venta aceptada / ganada"],
  ["account_created", "Cuenta creada", "Ya tiene acceso a WebProfit"],
  ["page_created", "Página creada", "El proyecto/página ya existe"],
  ["catalog_loading", "Cargando ítems", "Empezó a cargar catálogo/ítems"],
  ["business_setup", "Puesta a punto", "Configuración real del negocio"],
  ["stock_setup", "Stock", "Stock revisado/configurado"],
  ["site_review", "Revisión web", "Edición y control final de la página"],
  ["capabilities_ready", "Capacidades listas", "Puede usar el sistema completo"],
  ["official", "Cliente oficial", "Implementación completa"],
  ["paused", "Estable / pausa", "No requiere seguimiento operativo"],
];

const JOURNEY_INDEX = Object.fromEntries(JOURNEY.map(([value], index) => [value, index]));

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function sectionFor(index) {
  if (index <= 6) return "Venta";
  if (index <= 13) return "Implementación";
  return "Estable";
}

export default function OwnerClientCrmJourneyPanel({ client, onClientUpdated, onRefresh }) {
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const current = String(client?.crm?.journeyStep || (client?.relationshipType === "client" ? "official" : "cold"));
  const currentIndex = JOURNEY_INDEX[current] ?? 0;
  const cadence = String(client?.crm?.followUpCadence || "weekly");
  const currentLabel = JOURNEY[currentIndex]?.[1] || "Nuevo";
  const nextStep = JOURNEY[Math.min(currentIndex + 1, JOURNEY.length - 1)]?.[0] || current;

  const update = async (payload) => {
    setSaving(true);
    setError("");
    try {
      const response = await ownerApi.updateClient(idOf(client), payload);
      if (response?.client) onClientUpdated?.(response.client);
      await onRefresh?.();
      return response?.client || null;
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar el CRM."));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const setJourney = (step) => update({
    crmJourneyStep: step,
    ...(step === "official" || step === "paused" ? { crmFollowUpCadence: "none", crmNextAction: "", crmNextActionAt: "" } : {}),
  });

  const setCadence = (nextCadence) => update({ crmFollowUpCadence: nextCadence, crmFollowUpWeekday: 1 });

  const registerMessage = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await ownerApi.createInteraction(idOf(client), {
        kind: "message",
        channel: client?.preferredChannel || "whatsapp",
        direction: "outbound",
        summary: message.trim() || "Seguimiento enviado",
        source: { kind: "manual" },
      });
      if (response?.client) onClientUpdated?.(response.client);
      setMessage("");
      await onRefresh?.();
    } catch (err) {
      setError(formatApiError(err, "No pude registrar el seguimiento."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.1} alignItems={{ md: "flex-start" }}>
        <Box>
          <Typography sx={{ fontWeight: 1000 }}>CRM · {currentLabel}</Typography>
          <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>Venta + implementación en una sola ficha. Cliente oficial recién cuando la puesta a punto está completa.</Typography>
        </Box>
        <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
          <Chip size="small" icon={cadence === "weekly" ? <RepeatRoundedIcon /> : cadence === "none" ? <PauseRoundedIcon /> : undefined} label={cadence === "weekly" ? "Seguimiento · lunes" : cadence === "none" ? "Sin seguimiento" : "Seguimiento manual"} sx={{ fontWeight: 900 }} />
          {saving ? <CircularProgress size={20} /> : null}
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}

      {["Venta", "Implementación", "Estable"].map((section) => {
        const rows = JOURNEY.map((row, index) => ({ row, index })).filter(({ index }) => sectionFor(index) === section);
        return (
          <Box key={section} sx={{ mt: 1.35 }}>
            <Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>{section}</Typography>
            <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap sx={{ mt: .55 }}>
              {rows.map(({ row: [value, label], index }) => (
                <Chip
                  key={value}
                  clickable={!saving}
                  onClick={() => setJourney(value)}
                  label={label}
                  color={value === current ? "primary" : "default"}
                  variant={index <= currentIndex ? "filled" : "outlined"}
                  sx={{ fontWeight: value === current ? 1000 : 800 }}
                />
              ))}
            </Stack>
          </Box>
        );
      })}

      <Divider sx={{ my: 1.4 }} />
      <Stack direction={{ xs: "column", md: "row" }} spacing={.8} alignItems={{ md: "center" }}>
        <TextField
          fullWidth
          size="small"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Qué le mandaste / qué pasó (opcional)"
          onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); if (!saving) registerMessage(); } }}
        />
        <Button variant="contained" startIcon={<ForumRoundedIcon />} disabled={saving} onClick={registerMessage} sx={{ textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}>Registrar seguimiento</Button>
        {currentIndex < JOURNEY.length - 1 ? <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} disabled={saving} onClick={() => setJourney(nextStep)} sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}>Avanzar</Button> : null}
      </Stack>
      <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mt: .85 }}>
        <Button size="small" disabled={saving || cadence === "weekly"} onClick={() => setCadence("weekly")} sx={{ textTransform: "none", fontWeight: 850 }}>Seguir cada lunes</Button>
        <Button size="small" disabled={saving || cadence === "manual"} onClick={() => setCadence("manual")} sx={{ textTransform: "none", fontWeight: 850 }}>Manual</Button>
        <Button size="small" color="inherit" disabled={saving || cadence === "none"} onClick={() => setCadence("none")} sx={{ textTransform: "none", fontWeight: 850 }}>Pausar seguimiento</Button>
      </Stack>
    </Box>
  );
}
